import React, { createContext, useContext, useState, useEffect } from 'react';

const RoleContext = createContext();

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

export const COORDINATOR_ROLES = {
  SHIPMENT: 'shipment_coordinator',
  BOX_CONTENTS: 'box_contents_coordinator', 
  PRODUCT_LABEL: 'product_label_coordinator'
};

export const ROLE_LABELS = {
  [COORDINATOR_ROLES.SHIPMENT]: 'Shipment Coordinator',
  [COORDINATOR_ROLES.BOX_CONTENTS]: 'Box Contents Coordinator',
  [COORDINATOR_ROLES.PRODUCT_LABEL]: 'Product Label Coordinator'
};

export const RoleProvider = ({ children }) => {
  const [currentRole, setCurrentRole] = useState(() => {
    // Load from localStorage or default to shipment coordinator
    return localStorage.getItem('coordinatorRole') || COORDINATOR_ROLES.SHIPMENT;
  });

  // Save role to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('coordinatorRole', currentRole);
  }, [currentRole]);

  // Check if current user can see left panel (product listing, quantities, print buttons)
  const canViewLeftPanel = () => {
    return currentRole === COORDINATOR_ROLES.SHIPMENT || currentRole === COORDINATOR_ROLES.PRODUCT_LABEL;
  };

  // Check if current user can edit left panel items
  const canEditLeftPanel = () => {
    return currentRole === COORDINATOR_ROLES.SHIPMENT || currentRole === COORDINATOR_ROLES.PRODUCT_LABEL;
  };

  // Check if current user can see box panel normally (not grayscale)
  const canViewBoxPanel = () => {
    return currentRole === COORDINATOR_ROLES.SHIPMENT || currentRole === COORDINATOR_ROLES.BOX_CONTENTS;
  };

  // Check if current user can edit box contents
  const canEditBoxContents = () => {
    return currentRole === COORDINATOR_ROLES.SHIPMENT || currentRole === COORDINATOR_ROLES.BOX_CONTENTS;
  };

  // Check if box panel should be in read-only grayscale mode
  const isBoxPanelReadOnly = () => {
    return currentRole === COORDINATOR_ROLES.PRODUCT_LABEL;
  };

  const value = {
    currentRole,
    setCurrentRole,
    canViewLeftPanel,
    canEditLeftPanel,
    canViewBoxPanel,
    canEditBoxContents,
    isBoxPanelReadOnly,
    COORDINATOR_ROLES,
    ROLE_LABELS
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};